import { ChangeEvent, DragEvent, useCallback, useMemo, useReducer, useRef, useState } from 'react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Api } from '@.api';
import { Layout } from '@.layout';
import { Button } from '@.ui';
import { commitFolder } from '@/features/FolderImport/api';
import { extractFilesFromDataTransfer } from '@/features/FolderImport/lib/dropZone';
import { preflight } from '@/features/FolderImport/lib/preflight';
import { clearSnapshot } from '@/features/FolderImport/lib/storage';
import { FolderUploadOrchestrator } from '@/features/FolderImport/model/orchestrator';
import { ManifestEntry, PreflightResult } from '@/features/FolderImport/types';
import { LinkHelper } from '@/Services';
import { CheckboxXlsImportResponse, CheckboxXlsImportRow } from '@/Services/api/methods';

type ImportState = 'idle' | 'preflight' | 'preflight-ready' | 'uploading' | 'committing' | 'done' | 'error';

const th: React.CSSProperties = {
    padding: '8px 12px',
    borderBottom: '1px solid #d9cec5',
    whiteSpace: 'nowrap',
    textAlign: 'left',
    background: '#f5f0eb',
};
const td: React.CSSProperties = {
    padding: '6px 12px',
    borderBottom: '1px solid #ede8e3',
    whiteSpace: 'nowrap',
};

function isOverwritable(row: CheckboxXlsImportRow): boolean {
    if (row.status !== 'skipped' || !row.existing_id) return false;
    // The parser emits the warning "multiple images in row, only one is allowed" when image_urls > 1.
    // In that case the operator must fix the file; overwrite is not allowed.
    if ((row.image_urls?.length ?? 0) > 1) return false;
    return true;
}

function collectDuplicateGroups(rows: CheckboxXlsImportRow[]): number[][] {
    const seen = new Set<string>();
    const groups: number[][] = [];
    for (const r of rows) {
        if (!r.is_duplicate_in_file || !r.duplicate_rows?.length) continue;
        const key = [...r.duplicate_rows].sort((a, b) => a - b).join(',');
        if (seen.has(key)) continue;
        seen.add(key);
        groups.push([...r.duplicate_rows].sort((a, b) => a - b));
    }
    return groups;
}

/**
 * Renders a price tier as "30ml / 1.0oz: $28".
 * Zero volumes are dropped (e.g. "1.0oz: $28" when ml=0). The lenient
 * import logic accepts a tier with only one of the two volumes – operator
 * sees exactly what was imported.
 */
function formatPriceTier(p: { volume_ml: number; volume_fl_oz: number; price: number }): string {
    const parts: string[] = [];
    if (p.volume_ml > 0) parts.push(`${p.volume_ml}ml`);
    if (p.volume_fl_oz > 0) parts.push(`${p.volume_fl_oz}oz`);
    const volume = parts.length ? parts.join(' / ') : '—';
    return `${volume}: $${p.price}`;
}

interface ResultTableProps {
    rows: CheckboxXlsImportRow[];
    selected: Set<number>;
    onToggle: (row: number) => void;
    onToggleAll: () => void;
    allSelected: boolean;
    hasOverwritable: boolean;
}

const ResultTable: React.FC<ResultTableProps> = ({ rows, selected, onToggle, onToggleAll, allSelected, hasOverwritable }) => {
    const [hideEmptyColumns, setHideEmptyColumns] = useState(true);

    const emptyColumns = useMemo((): Set<string> => {
        const empty = new Set<string>();
        const textCols = [
            'udi',
            'name',
            'brand',
            'type',
            'gender',
            'age',
            'strength',
            'occasion',
            'ingredients',
            'characters',
            'description',
            'reason',
        ] as const;
        for (const col of textCols) {
            if (rows.every((r) => !r[col as keyof CheckboxXlsImportRow])) empty.add(col);
        }
        if (rows.every((r) => !r.prices?.length)) empty.add('prices');
        if (rows.every((r) => !r.id && !r.existing_id)) empty.add('product');
        if (rows.every((r) => !r.image_urls?.length)) empty.add('image');
        return empty;
    }, [rows]);

    const show = (col: string) => !hideEmptyColumns || !emptyColumns.has(col);

    return (
        <div style={{ overflowX: 'auto', marginTop: '16px', fontFamily: '"DM Sans", sans-serif' }}>
            <label
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 13,
                    cursor: 'pointer',
                    marginBottom: 10,
                }}
            >
                <input type="checkbox" checked={hideEmptyColumns} onChange={(e) => setHideEmptyColumns(e.target.checked)} />
                Hide empty columns
            </label>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                    <tr>
                        {hasOverwritable && (
                            <th style={th}>
                                <input type="checkbox" checked={allSelected} onChange={onToggleAll} title="Select all overwritable" />
                            </th>
                        )}
                        <th style={th}>Row #</th>
                        <th style={th}>Status</th>
                        {show('udi') && <th style={th}>UDI</th>}
                        {show('name') && <th style={th}>Name</th>}
                        {show('brand') && <th style={th}>Brand</th>}
                        {show('type') && <th style={th}>Type</th>}
                        {show('gender') && <th style={th}>Sex</th>}
                        {show('age') && <th style={th}>Age</th>}
                        {show('strength') && <th style={th}>Strength</th>}
                        {show('occasion') && <th style={th}>Occasion</th>}
                        {show('ingredients') && <th style={th}>Kind of Scent</th>}
                        {show('characters') && <th style={th}>Character</th>}
                        {show('description') && <th style={th}>Product Info</th>}
                        {show('prices') && <th style={th}>Prices</th>}
                        {show('image') && <th style={th}>Image</th>}
                        {show('product') && <th style={th}>Product</th>}
                        {show('reason') && <th style={th}>Reason</th>}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => {
                        const canSelect = isOverwritable(r);
                        const isSuccess = r.status === 'ok' || r.status === 'updated';
                        const isDupHighlight = r.is_duplicate_in_file && !isSuccess;
                        const bg = isDupHighlight ? '#ffacac' : isSuccess ? '#f0faf0' : '#fdf4f4';
                        return (
                            <tr key={r.row} style={{ background: bg }}>
                                {hasOverwritable && (
                                    <td style={td}>{canSelect && <input type="checkbox" checked={selected.has(r.row)} onChange={() => onToggle(r.row)} />}</td>
                                )}
                                <td style={td}>{r.row}</td>
                                <td style={{ ...td, fontWeight: 500 }}>{r.status}</td>
                                {show('udi') && <td style={td}>{r.udi || '—'}</td>}
                                {show('name') && <td style={td}>{r.name || '—'}</td>}
                                {show('brand') && <td style={td}>{r.brand || '—'}</td>}
                                {show('type') && <td style={td}>{r.type || '—'}</td>}
                                {show('gender') && <td style={td}>{r.gender || '—'}</td>}
                                {show('age') && <td style={td}>{r.age || '—'}</td>}
                                {show('strength') && <td style={td}>{r.strength || '—'}</td>}
                                {show('occasion') && <td style={td}>{r.occasion || '—'}</td>}
                                {show('ingredients') && <td style={td}>{r.ingredients || '—'}</td>}
                                {show('characters') && <td style={td}>{r.characters || '—'}</td>}
                                {show('description') && (
                                    <td
                                        style={{
                                            ...td,
                                            maxWidth: 160,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                        title={r.description || undefined}
                                    >
                                        {r.description || '—'}
                                    </td>
                                )}
                                {show('prices') && (
                                    <td style={{ ...td, whiteSpace: 'pre', minWidth: 140 }}>
                                        {r.prices?.length ? r.prices.map(formatPriceTier).join('\n') : '—'}
                                    </td>
                                )}
                                {show('image') && (
                                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                        {r.image_urls?.length ? (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 2,
                                                }}
                                            >
                                                {r.image_urls.map((url, i) => (
                                                    <img
                                                        key={i}
                                                        src={url}
                                                        alt=""
                                                        style={{
                                                            height: 40,
                                                            width: 40,
                                                            objectFit: 'cover',
                                                            borderRadius: 4,
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        ) : (
                                            '—'
                                        )}
                                    </td>
                                )}
                                {show('product') && (
                                    <td style={td}>
                                        {r.id || r.existing_id ? (
                                            <a href={`/data/products/${r.id ?? r.existing_id}`} target="_blank" rel="noreferrer">
                                                #{r.id ?? r.existing_id}
                                            </a>
                                        ) : (
                                            '—'
                                        )}
                                    </td>
                                )}
                                {show('reason') && <td style={{ ...td, color: canSelect ? '#c0392b' : undefined }}>{r.reason || '—'}</td>}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export const ProductsXlsImport: React.FC = () => {
    const navigate = useNavigate();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [state, setState] = useState<ImportState>('idle');
    const [pf, setPf] = useState<PreflightResult | null>(null);
    const [progress, setProgress] = useState<{ confirmed: number; total: number }>({ confirmed: 0, total: 0 });
    const [result, setResult] = useState<CheckboxXlsImportResponse | null>(null);
    const [rows, setRows] = useState<CheckboxXlsImportRow[]>([]);
    const [errorMsg, setErrorMsg] = useState('');
    const [, resetFileKey] = useReducer((p: number) => p + 1, 0);
    const [selected, setSelected] = useState<Set<number>>(new Set());
    const [overwriting, setOverwriting] = useState(false);
    const [overwriteMsg, setOverwriteMsg] = useState('');

    const breadcrumbs = [
        { name: 'Products', link: LinkHelper.dataProducts },
        { name: 'Import XLS', current: true },
    ];

    const [isDragOver, setIsDragOver] = useState(false);

    const runPreflight = useCallback(async (files: File[] | FileList) => {
        const list = Array.from(files as File[]);
        if (list.length === 0) return;

        setState('preflight');
        setErrorMsg('');
        setPf(null);

        const res = await preflight(list);

        if ('error' in res) {
            setErrorMsg(res.error);
            setState('error');
            return;
        }

        setPf(res);
        setState('preflight-ready');
    }, []);

    const handlePick = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const files = event.target.files;
            if (files) runPreflight(files);
        },
        [runPreflight]
    );

    const triggerPicker = useCallback(() => {
        inputRef.current?.click();
    }, []);

    const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback(
        async (e: DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragOver(false);

            const files = await extractFilesFromDataTransfer(e.dataTransfer);
            if (files.length === 0) {
                setErrorMsg('No files found in the dropped item.');
                setState('error');
                return;
            }

            runPreflight(files);
        },
        [runPreflight]
    );

    const handleUpload = useCallback(async () => {
        if (!pf || pf.matched.length === 0) return;

        setState('uploading');
        setProgress({ confirmed: 0, total: pf.matched.length });

        const orchestrator = new FolderUploadOrchestrator('create');
        const batchId = orchestrator.getBatchId();

        let manifest: ManifestEntry[] = [];
        try {
            manifest = await orchestrator.run(pf.matched, {
                onProgress: (confirmed, total) => setProgress({ confirmed, total }),
            });
        } catch (e) {
            setErrorMsg('Upload failed: ' + (e instanceof Error ? e.message : 'unknown'));
            setState('error');
            return;
        }

        if (manifest.length === 0) {
            setErrorMsg('All file uploads failed.');
            setState('error');
            return;
        }

        setState('committing');

        const commitRes = await commitFolder({
            xls: pf.xlsFile,
            batchId,
            manifest,
            overwrite: false,
        });

        if (!commitRes.ok) {
            if (commitRes.mismatch) {
                const m = commitRes.mismatch;
                const parts: string[] = [];
                if (m.missing.length) parts.push(`missing: ${m.missing.length}`);
                if (m.extra.length) parts.push(`extra: ${m.extra.length}`);
                if (m.checksum_mismatch.length) parts.push(`checksum mismatch: ${m.checksum_mismatch.length}`);
                setErrorMsg(`Commit rejected. ${parts.join(', ')}.`);
            } else {
                setErrorMsg(commitRes.message ?? `Commit failed (HTTP ${commitRes.status}).`);
            }
            setState('error');
            return;
        }

        clearSnapshot(batchId);

        const data: CheckboxXlsImportResponse = {
            message: '',
            imported: commitRes.summary.imported,
            skipped: commitRes.summary.skipped,
            rows: commitRes.summary.rows as unknown as CheckboxXlsImportRow[],
        };
        setResult(data);
        setRows(data.rows);
        setState('done');
    }, [pf]);

    const handleRetry = () => {
        setPf(null);
        setProgress({ confirmed: 0, total: 0 });
        setResult(null);
        setRows([]);
        setErrorMsg('');
        setSelected(new Set());
        setOverwriteMsg('');
        if (inputRef.current) inputRef.current.value = '';
        resetFileKey();
        setState('idle');
    };

    const overwritableRows = rows.filter(isOverwritable);
    const allSelected = overwritableRows.length > 0 && overwritableRows.every((r) => selected.has(r.row));

    const toggleRow = (rowNum: number) => {
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(rowNum) ? next.delete(rowNum) : next.add(rowNum);
            return next;
        });
    };

    const toggleAll = () => {
        if (allSelected) {
            setSelected(new Set());
        } else {
            setSelected(new Set(overwritableRows.map((r) => r.row)));
        }
    };

    const handleOverwrite = () => {
        const toOverwrite = rows.filter((r) => selected.has(r.row));
        if (!toOverwrite.length) return;

        setOverwriting(true);
        setOverwriteMsg('');

        Api.products()
            .importProductsXlsOverwrite(toOverwrite)
            .onSuccess(({ data }) => {
                setRows((prev) =>
                    prev.map((r) => {
                        const updated = data.rows.find((u) => u.row === r.row);
                        if (!updated) return r;
                        return {
                            ...r,
                            status: updated.status,
                            reason: updated.reason ?? r.reason,
                            is_duplicate_in_file: updated.is_duplicate_in_file ?? r.is_duplicate_in_file,
                            duplicate_rows: updated.duplicate_rows ?? r.duplicate_rows,
                        };
                    })
                );
                setSelected(new Set());
                setOverwriteMsg(`${data.updated} product${data.updated !== 1 ? 's' : ''} updated${data.failed ? `, ${data.failed} failed` : ''}.`);
                setOverwriting(false);
            })
            .onErrors(({ message }) => {
                setOverwriteMsg(message || 'Overwrite failed');
                setOverwriting(false);
            });
    };

    const groupedWarnings = useMemo(() => {
        if (!pf) return null;
        const map: Record<string, string[]> = {};
        for (const w of pf.warnings) {
            (map[w.kind] ||= []).push(w.text);
        }
        return map;
    }, [pf]);

    const warningLabel = (kind: string): string => {
        switch (kind) {
            case 'duplicate-prefix':
                return 'Duplicate filenames per row';
            case 'no-image':
                return 'Rows without an image';
            case 'no-row':
                return 'Files without a matching row';
            case 'invalid-name':
                return 'Files with invalid filename';
            case 'too-large':
                return 'Files larger than 1 MB';
            case 'unsupported-format':
                return 'Unsupported file format';
            case 'corrupted':
                return 'Corrupted or unreadable files';
            default:
                return kind;
        }
    };

    return (
        <Layout breadcrumbs={breadcrumbs}>
            <div style={{ fontFamily: '"DM Sans", sans-serif' }}>
                <h1>Import XLS</h1>

                <input
                    ref={inputRef}
                    type="file"
                    /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
                    /* @ts-ignore - webkitdirectory is non-standard but supported on all desktop browsers */
                    webkitdirectory=""
                    directory=""
                    multiple
                    onChange={handlePick}
                    style={{ display: 'none' }}
                />

                {state === 'idle' && (
                    <>
                        <p style={{ marginBottom: 12 }}>
                            Drop a folder here or click to select. The folder must contain one XLSX file and image files alongside it. Image filenames must
                            follow: <code>{'{row_number}.{any_name}.{ext}'}</code>. Supported formats: jpg, jpeg, png, bmp. Max 1 MB per file.
                        </p>
                        <div
                            onClick={triggerPicker}
                            onDragOver={handleDragOver}
                            onDragEnter={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            style={{
                                border: `2px dashed ${isDragOver ? '#a06a3f' : '#c9bda9'}`,
                                background: isDragOver ? '#f5ece0' : '#faf6f0',
                                borderRadius: 8,
                                padding: '36px 24px',
                                textAlign: 'center',
                                cursor: 'pointer',
                                transition: 'background 120ms, border-color 120ms',
                                userSelect: 'none',
                            }}
                        >
                            <div style={{ fontSize: 16, marginBottom: 6, color: '#6e4a2e' }}>
                                {isDragOver ? 'Release to add the folder' : 'Drop folder here or click to browse'}
                            </div>
                            <div style={{ fontSize: 12, color: '#9a8a73' }}>One XLSX + image files inside</div>
                        </div>
                        <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                            <Button color="brown" onClick={triggerPicker}>
                                Select folder
                            </Button>
                            <Button onClick={() => navigate(LinkHelper.dataProducts)}>Cancel</Button>
                        </div>
                    </>
                )}

                {state === 'preflight' && <p>Checking files…</p>}

                {state === 'preflight-ready' && pf && (
                    <div>
                        <h3 style={{ marginTop: 8 }}>Ready to upload</h3>
                        <ul style={{ listStyle: 'disc', paddingLeft: 24 }}>
                            <li>XLS: {pf.xlsFile.name}</li>
                            <li>Data rows in XLS: {pf.rowCount}</li>
                            <li>Images ready to upload: {pf.matched.length}</li>
                            <li>Warnings: {pf.warnings.length}</li>
                        </ul>
                        <br />
                        {groupedWarnings && Object.keys(groupedWarnings).length > 0 && (
                            <div style={{ marginTop: 12 }}>
                                <strong>Warnings:</strong>
                                {Object.entries(groupedWarnings).map(([kind, msgs], idx) => (
                                    <details key={kind} open style={{ marginTop: idx === 0 ? 0 : 14 }}>
                                        <summary>
                                            {warningLabel(kind)} ({msgs.length})
                                        </summary>
                                        <ul style={{ paddingLeft: 24 }}>
                                            {msgs.map((m, i) => (
                                                <li key={i}>{m}</li>
                                            ))}
                                        </ul>
                                    </details>
                                ))}
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                            <Button color="brown" disabled={pf.matched.length === 0} onClick={handleUpload}>
                                Upload ({pf.matched.length})
                            </Button>
                            <Button onClick={handleRetry}>Cancel</Button>
                        </div>
                    </div>
                )}

                {state === 'uploading' && (
                    <>
                        <p>
                            Uploading images: {progress.confirmed} / {progress.total}
                        </p>
                        <progress value={progress.confirmed} max={progress.total} style={{ width: '100%', height: 22 }} />
                    </>
                )}

                {state === 'committing' && <p>Finalizing import…</p>}

                {state === 'done' && result && (
                    <>
                        <p>
                            Import complete: <strong>{result.imported}</strong> imported, <strong>{result.skipped}</strong> skipped.
                        </p>

                        {(() => {
                            const dupGroups = collectDuplicateGroups(rows);
                            if (!dupGroups.length) return null;
                            return (
                                <div
                                    style={{
                                        background: '#ffacac',
                                        border: '1px solid #c0392b',
                                        borderRadius: 4,
                                        padding: '8px 12px',
                                        marginTop: 8,
                                        fontSize: 13,
                                    }}
                                >
                                    <strong>Duplicate name+brand groups in file ({dupGroups.length}):</strong>
                                    <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                                        {dupGroups.map((group, i) => {
                                            const winner = group[group.length - 1];
                                            const others = group.slice(0, -1);
                                            return (
                                                <li key={i}>
                                                    rows {group.join(', ')} – row {winner} takes precedence, row{others.length > 1 ? 's' : ''}{' '}
                                                    {others.join(', ')} skipped
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            );
                        })()}

                        {rows.length > 0 && (
                            <ResultTable
                                rows={rows}
                                selected={selected}
                                onToggle={toggleRow}
                                onToggleAll={toggleAll}
                                allSelected={allSelected}
                                hasOverwritable={overwritableRows.length > 0}
                            />
                        )}

                        {overwriteMsg && (
                            <p
                                style={{
                                    marginTop: '12px',
                                    color: overwriteMsg.includes('failed') ? '#c0392b' : '#27ae60',
                                }}
                            >
                                {overwriteMsg}
                            </p>
                        )}

                        <div
                            style={{
                                display: 'flex',
                                gap: '12px',
                                marginTop: '16px',
                                flexWrap: 'wrap',
                            }}
                        >
                            <Button color="brown" onClick={() => navigate(LinkHelper.dataProducts)}>
                                Back to list
                            </Button>
                            {overwritableRows.length > 0 && (
                                <Button color="brown" disabled={overwriting || selected.size === 0} onClick={handleOverwrite}>
                                    {overwriting ? 'Overwriting...' : `Overwrite selected (${selected.size})`}
                                </Button>
                            )}
                            <Button onClick={handleRetry}>Import another file</Button>
                        </div>
                    </>
                )}

                {state === 'error' && (
                    <>
                        <p style={{ color: '#c0392b' }}>{errorMsg}</p>
                        <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                            <Button color="brown" onClick={handleRetry}>
                                Try again
                            </Button>
                            <Button onClick={() => navigate(LinkHelper.dataProducts)}>Back to list</Button>
                        </div>
                    </>
                )}
            </div>
        </Layout>
    );
};
